import axios from 'axios';

const resolveBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl && envUrl.trim()) {
    return envUrl.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }

  return '';
};

const BACKEND_URL = resolveBackendUrl();

if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.warn('REACT_APP_BACKEND_URL is not set and window.location.origin is unavailable. API calls will fail.');
}

export const apiClient = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  timeout: 15000,
});

const SESSION_TOKEN_STORAGE_KEY = 'hardaySessionToken';

// Attaches the signed session JWT (if any) to every outgoing request so the
// backend can verify the caller instead of trusting a bare user id.
export const setSessionToken = (token) => {
  if (token) {
    localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token);
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    delete apiClient.defaults.headers.common.Authorization;
  }
};

export const getStoredSessionToken = () => localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);

// Restore the Authorization header on module load (e.g. after a page reload)
// so the very first request already carries the token.
const initialStoredToken = getStoredSessionToken();
if (initialStoredToken) {
  apiClient.defaults.headers.common.Authorization = `Bearer ${initialStoredToken}`;
}

// Messages/chat endpoints often run on flaky mobile networks where the default
// 15s budget is too tight and surfaces a raw "timeout of 15000ms exceeded"
// error. Give chat reads a longer, dedicated timeout without slowing down the
// rest of the app.
export const CHAT_REQUEST_TIMEOUT = 28000;

// Returns true when a request failed because of a timeout or a lost/flaky
// network connection (i.e. no HTTP response was received), so callers can show
// a friendly "Low network. Tap to retry." message instead of the raw error.
export const isNetworkOrTimeoutError = (error) => {
  if (!error) return false;
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK') return true;
  if (typeof error.message === 'string' && /timeout|network/i.test(error.message)) return true;
  // A dropped connection has no HTTP response attached.
  if (error.request && !error.response) return true;
  return false;
};

export const api = {
  login: (payload) => apiClient.post('/users/login', payload),
  getUser: (userId) => apiClient.get(`/users/${userId}`),
  getChats: (userId) => apiClient.get(`/users/${userId}/chats`, { timeout: CHAT_REQUEST_TIMEOUT }),
  getUnreadMessageCount: (userId) => apiClient.get('/messages/unread-count', { params: { userId } }),
  getChatThread: (userId, otherUserId) => apiClient.get(`/users/${userId}/chats/${otherUserId}`, { timeout: CHAT_REQUEST_TIMEOUT }),
  sendMessage: (payload) => apiClient.post('/chats/messages', payload),
  reportChat: (userId, otherUserId, payload) => apiClient.post(`/users/${userId}/chats/${otherUserId}/report`, payload),
  exchangeGoogleLoginToken: (token, deviceBinding = {}) => apiClient.post('/auth/google/session', { token, ...deviceBinding }),
  migrateLegacySession: (userId) => apiClient.post('/auth/session/migrate', { userId }),
  adminLogin: (payload) => apiClient.post('/admin/auth/login', payload),
  getAdminSession: () => apiClient.get('/admin/auth/me'),
  adminLogout: () => apiClient.post('/admin/auth/logout', {}),
  updateAdminCredentials: (payload) => apiClient.put('/admin/auth/settings', payload),
  updateUserProfile: (userId, payload) => apiClient.put(`/users/${userId}/profile`, payload),
  getUserReferrals: (userId) => apiClient.get(`/users/${userId}/referrals`),
  getUserTickets: (userId) => apiClient.get(`/users/${userId}/tickets`),
  topup: (userId, amount) => apiClient.post(`/users/${userId}/topup`, { amount }),
  solvePuzzle: (userId, payload = {}) => apiClient.post(`/users/${userId}/solve-puzzle`, payload),
  createFreePuzzleUnlockSession: (userId, entryNumber) => apiClient.post('/free-puzzle/unlock-session', { userId, entryNumber }),
  getFreePuzzleSessionStatus: (userId, entryNumber) => apiClient.get('/free-puzzle/session-status', { params: { userId, entryNumber } }),
  submitFreePuzzle: (userId, payload = {}) => apiClient.post('/free-puzzle/submit', { userId, ...payload }),
  createWithdrawal: (userId, payload) => apiClient.post(`/users/${userId}/withdrawals`, payload),
  deleteOwnAccount: (userId) => apiClient.delete(`/users/${userId}`),
  updateNotificationSettings: (userId, payload) => apiClient.put(`/users/${userId}/notification-settings`, payload),
  markWinnerAnnouncementSeen: (userId, winnerId) => apiClient.post(`/users/${userId}/winner-announcement/seen`, { winnerId }),
  heartbeat: (userId) => apiClient.post(`/users/${userId}/heartbeat`),
  getPoolStatus: () => apiClient.get('/pool/status'),
  getWinners: (limit = 5) => apiClient.get(`/winners?limit=${limit}`),
  getAdminConfig: () => apiClient.get('/config/public'),
  updateAdminConfig: (payload) => apiClient.put('/admin/config', payload),
  getAdminNotifications: () => apiClient.get('/admin/notifications'),
  createAdminNotification: (payload) => apiClient.post('/admin/notifications', payload),
  updateAdminNotificationStatus: (notificationId, payload) => apiClient.patch(`/admin/notifications/${notificationId}/status`, payload),
  manualAddToPool: (amount) => apiClient.post('/admin/pool/manual-add', { amount }),
  getAdminWithdrawals: (params = {}) => apiClient.get('/admin/withdrawals', { params }),
  updateWithdrawalStatus: (withdrawalId, payload) => apiClient.patch(`/admin/withdrawals/${withdrawalId}/status`, payload),

  // Manual UPI Top-Up Requests (admin approval). The user pays manually to
  // the rotating UPI ID shown in the Top-up modal, then presses "I Paid"
  // which confirms the request with their payer UPI ID for reconciliation.
  createTopupRequest: (userId, amount) => apiClient.post(`/users/${userId}/topup-requests`, { amount }, { timeout: 45000 }),
  allocateTopupRequest: (userId, amount) => apiClient.post(`/users/${userId}/topup-requests/allocate`, { amount }, { timeout: 45000 }),
  // payload: { amount, orderNote, upiId, payerUpiId }
  confirmTopupRequest: (userId, payload) => apiClient.post(`/users/${userId}/topup-requests/confirm`, payload, { timeout: 45000 }),
  getUserTopupRequests: (userId, params = {}) => apiClient.get(`/users/${userId}/topup-requests`, { params }),
  getAdminTopupRequests: (params = {}) => apiClient.get('/admin/topup-requests', { params }),
  updateTopupRequestStatus: (requestId, payload) => apiClient.patch(`/admin/topup-requests/${requestId}/status`, payload),
  updateTopupUpiIds: (payload) => apiClient.put('/admin/topup-upi-ids', payload),

  searchAdminUsers: (params = {}) => apiClient.get('/admin/users/search', { params }),
  getAdminUserDetail: (userId) => apiClient.get(`/admin/users/${userId}`),
  saveAdminUserProfile: (userId, payload) => apiClient.put(`/admin/users/${userId}`, payload),
  deleteAdminUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
  updateAdminUserProfile: (userId, payload) => apiClient.patch(`/admin/users/${userId}/profile`, payload),
  setAdminUserCredits: (userId, value) => apiClient.patch(`/admin/users/${userId}/credits`, { value }),
  setAdminUserProfileScore: (userId, value) => apiClient.patch(`/admin/users/${userId}/profile-score`, { value }),
  setAdminUserBlock: (userId, value) => apiClient.patch(`/admin/users/${userId}/block`, { value }),
  setAdminUserWithdrawalsPause: (userId, value) => apiClient.patch(`/admin/users/${userId}/withdrawals-pause`, { value }),
  setAdminUserSwan: (userId) => apiClient.post(`/admin/users/${userId}/swan`, {}),

  getAdminUserWithdrawals: (userId) => apiClient.get(`/admin/users/${userId}/withdrawals`),
  getAdminUserTopups: (userId) => apiClient.get(`/admin/users/${userId}/topups`),
  getAdminUserReferralEarnings: (userId) => apiClient.get(`/admin/users/${userId}/referral-earnings`),
  getAdminUserReferrals: (userId) => apiClient.get(`/admin/users/${userId}/referrals`),
  getAdminUserPoolWins: (userId) => apiClient.get(`/admin/users/${userId}/pool-wins`),

  getAdminUsersAnalytics: (params = {}) => apiClient.get('/admin/analytics/users', { params }),
  getAdminUsersByPeriod: (params = {}) => apiClient.get('/admin/users/by-period', { params }),
  getAdminReferralsAnalytics: (params = {}) => apiClient.get('/admin/analytics/referrals', { params }),
  getAdminTopReferrers: (limit = 20) => apiClient.get('/admin/analytics/top-referrers', { params: { limit } }),
  getAdminDemographics: () => apiClient.get('/admin/demographics'),
};
